import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import FormLabel from '@mui/material/FormLabel';
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '@mui/material/Typography';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { alpha, useTheme } from '@mui/material/styles';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckIcon from '@mui/icons-material/Check';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Controller, useForm, useFormContext } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object, string } from 'yup';
import { useNavigate } from 'react-router';
import toast from 'react-hot-toast';
import { organizationApi } from 'src/api/organizationApi';
import { pimApi } from 'src/api/pimApi';
import { userApi } from 'src/api/userApi';
import { Button } from 'src/common';
import { GenericModal } from 'src/common/modal/modal';
import { RHFCustomSelect, RHFProvider, RHFTextField } from 'src/common/rhf';
import { useUserProfile } from 'src/contexts/UserProfileContext';
import timezonesData from 'src/constants/timezones.json';
import {
  CopyProductsAndPackagesStep,
  hasInvalidCopyPrices,
  mapPricebooksToCopyItems,
  toCopyPriceBookPayload,
  type CopyPriceItem,
} from './CopyProductsAndPackagesStep';
import { FinalizeStep } from './FinalizeStep';
import {
  clearPendingClientSubscription,
  savePendingClientSubscription,
} from './pendingClientSubscription';
import {
  SubscriptionCreateForm,
  SubscriptionFieldPreview,
  type SubscriptionCreateDraft,
} from 'src/pages/subscriptions/create-form';

export interface CreateClientFormValues extends Record<string, unknown> {
  organizationName: string;
  organizationTypeId: string;
  telephone: string;
  email: string;
  website: string;
  street: string;
  city: string;
  country: string;
  postalCode: string;
  timeZone: string;
  firstName: string;
  lastName: string;
  designation: string;
  language: string;
  contactTelephone: string;
  contactEmail: string;
}

interface CreateClientWizardProps {
  open: boolean;
  onClose: () => void;
  onCreated?: () => void;
  resumeClient?: {
    organizationId: string;
    organizationName: string;
  } | null;
}

type WizardPhase = 'form' | 'success' | 'subscription' | 'preview';

type SelectOption = { id: string; label: string; value: string };

const STEPS = [
  { id: 0, label: 'Company Details' },
  { id: 1, label: 'User Details' },
  { id: 2, label: 'Products & Packages' },
  { id: 3, label: 'Finalize' },
] as const;

const LAST_STEP = STEPS.length - 1;

const STEP_CAPTIONS = [
  'Company Details',
  'Client User Information',
  'Copy Products and Packages',
  'Review & Create',
] as const;

const COMPANY_STEP_FIELDS = [
  'organizationName',
  'organizationTypeId',
  'telephone',
  'email',
  'website',
  'street',
  'city',
  'country',
  'postalCode',
  'timeZone',
] as const;

const USER_STEP_FIELDS = [
  'firstName',
  'lastName',
  'designation',
  'language',
  'contactTelephone',
  'contactEmail',
] as const;

const EMAIL_FORMAT_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const DEFAULT_COUNTRY_OPTIONS: SelectOption[] = [
  { id: 'NL', label: 'Netherlands', value: 'NL' },
  { id: 'DE', label: 'Germany', value: 'DE' },
  { id: 'BE', label: 'Belgium', value: 'BE' },
  { id: 'US', label: 'USA', value: 'US' },
  { id: 'GB', label: 'UK', value: 'GB' },
];

const PREFERRED_TIMEZONES = timezonesData.preferred;
const ALL_TIMEZONES = timezonesData.timezones;

const COUNTRY_DEFAULT_TIMEZONE: Record<string, string> = {
  NL: 'Europe/Amsterdam',
  DE: 'Europe/Berlin',
  BE: 'Europe/Brussels',
  US: 'America/New_York',
  GB: 'Europe/London',
};

const LANGUAGE_OPTIONS: SelectOption[] = [
  { id: 'en', label: 'English (US)', value: 'en' },
  { id: 'nl', label: 'Dutch (NL)', value: 'nl' },
];

const countryLabelByValue: Record<string, string> = {
  NL: 'Netherlands',
  DE: 'Germany',
  BE: 'Belgium',
  US: 'USA',
  GB: 'UK',
};

const WEBSITE_REGEX = /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/[\w-./?%&=]*)?$/i;

const defaultValues: CreateClientFormValues = {
  organizationName: '',
  organizationTypeId: '',
  telephone: '',
  email: '',
  website: '',
  street: '',
  city: '',
  country: 'US',
  postalCode: '',
  timeZone: 'America/New_York',
  firstName: '',
  lastName: '',
  designation: '',
  language: 'en',
  contactTelephone: '',
  contactEmail: '',
};

const validationSchema = object({
  organizationName: string().trim().required('Company name is required'),
  organizationTypeId: string().trim().required('Company type is required'),
  telephone: string().trim().optional(),
  email: string().trim().required('Company email is required').email('Enter a valid email address'),
  website: string()
    .trim()
    .optional()
    .matches(WEBSITE_REGEX, {
      message: 'Enter a valid website URL',
      excludeEmptyString: true,
    }),
  street: string().trim().optional(),
  city: string().trim().optional(),
  country: string().trim().optional(),
  postalCode: string().trim().optional(),
  timeZone: string().trim().optional(),
  firstName: string().trim().required('First name is required'),
  lastName: string().trim().required('Last name is required'),
  designation: string().trim().optional(),
  language: string().trim().optional(),
  contactTelephone: string().trim().optional(),
  contactEmail: string()
    .trim()
    .required('Contact email is required')
    .email('Enter a valid email address'),
});

const resolveBackendErrorMessage = (error: any): string => {
  const errorData = error?.response?.data;

  if (typeof errorData?.message === 'string' && errorData.message.trim()) {
    return errorData.message;
  }

  if (Array.isArray(errorData?.message) && errorData.message.length > 0) {
    return String(errorData.message[0]);
  }

  if (typeof error?.message === 'string' && error.message.trim()) {
    return error.message;
  }

  return 'Failed to create client';
};

const SectionTitle = ({ title, icon }: { title: string; icon: ReactNode }) => {
  const theme = useTheme();

  return (
    <Box display="flex" alignItems="center" marginBottom={2} style={{ gap: 12 }}>
      <Box
        style={{
          width: 34,
          height: 34,
          borderRadius: 6,
          backgroundColor: alpha(theme.palette.primary.main, 0.12),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        {icon}
      </Box>
      <Typography
        style={{
          fontSize: 16,
          fontWeight: 600,
          color: theme.palette.text.primary,
        }}>
        {title}
      </Typography>
    </Box>
  );
};

const WizardStepper = ({ activeStep }: { activeStep: number }) => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const muted = theme.palette.text.disabled;
  const completedText = theme.palette.text.secondary;
  const inactiveBorder = theme.palette.divider;
  const inactiveFill = theme.palette.background.paper;

  return (
    <Box
      display="flex"
      alignItems="flex-start"
      justifyContent="center"
      marginBottom={3}>
      {STEPS.map((step, index) => {
        const isCompleted = activeStep > step.id;
        const isActive = activeStep === step.id;
        const isHighlighted = isCompleted || isActive;

        return (
          <Box key={step.id} display="flex" alignItems="flex-start">
            {index > 0 && (
              <Box
                style={{
                  width: 40,
                  height: 2,
                  marginTop: 13,
                  backgroundColor: isHighlighted ? primary : inactiveBorder,
                }}
              />
            )}
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              style={{ minWidth: 112, gap: 8 }}>
              <Box
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: isHighlighted ? primary : inactiveFill,
                  border: `2px solid ${isHighlighted ? primary : inactiveBorder}`,
                  color: isHighlighted ? theme.palette.primary.contrastText : muted,
                  fontSize: 13,
                  fontWeight: 600,
                }}>
                {isCompleted ? <CheckIcon style={{ fontSize: 16 }} /> : step.id + 1}
              </Box>
              <Typography
                style={{
                  fontSize: 12,
                  fontWeight: isActive ? 600 : 500,
                  color: isActive ? primary : isCompleted ? completedText : muted,
                  textAlign: 'center',
                  lineHeight: 1.3,
                }}>
                {step.label}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

const RHFTimezoneAutocomplete = ({
  name,
  outerLabel = 'TIME ZONE',
  placeholder = 'Search time zone',
}: {
  name: string;
  outerLabel?: string;
  placeholder?: string;
}) => {
  const theme = useTheme();
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <Box>
          <FormLabel
            style={{
              paddingBottom: '0.6rem',
              fontSize: 12,
              color: '#718096',
              display: 'block',
            }}>
            {outerLabel}
          </FormLabel>
          <Autocomplete
            options={ALL_TIMEZONES}
            value={field.value || null}
            onChange={(_event, value) => field.onChange(value || '')}
            onBlur={field.onBlur}
            filterOptions={(options, state) => {
              const query = state.inputValue.trim().toLowerCase();
              if (!query) {
                const defaults = [...PREFERRED_TIMEZONES];
                if (field.value && !defaults.includes(field.value)) {
                  return [field.value, ...defaults];
                }
                return defaults;
              }
              return options.filter((option) => option.toLowerCase().includes(query));
            }}
            getOptionLabel={(option) => option}
            isOptionEqualToValue={(option, value) => option === value}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={placeholder}
                error={Boolean(error)}
                helperText={error?.message}
                inputRef={field.ref}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: 50,
                    fontSize: 12,
                    backgroundColor: theme.palette.background.paper,
                  },
                }}
              />
            )}
            ListboxProps={{
              style: { fontSize: 12, maxHeight: 280 },
            }}
          />
        </Box>
      )}
    />
  );
};

const CreateClientWizard = ({
  open,
  onClose,
  onCreated,
  resumeClient,
}: CreateClientWizardProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { userData } = useUserProfile();
  const parentOrganizationId = userData?.organization?.id || '';
  const [activeStep, setActiveStep] = useState(0);
  const [phase, setPhase] = useState<WizardPhase>('form');
  const [createdOrganizationId, setCreatedOrganizationId] = useState<string | null>(null);
  const [createdOrganizationName, setCreatedOrganizationName] = useState('');
  const [subscriptionDraft, setSubscriptionDraft] = useState<SubscriptionCreateDraft | null>(
    null
  );
  const [organizationTypeOptions, setOrganizationTypeOptions] = useState<SelectOption[]>([]);
  const [isLoadingOrganizationTypes, setIsLoadingOrganizationTypes] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [copyItems, setCopyItems] = useState<CopyPriceItem[]>([]);
  const [isLoadingCopyItems, setIsLoadingCopyItems] = useState(false);

  const lastCheckedEmailRef = useRef<string | null>(null);
  const lastEmailCheckRef = useRef<boolean | null>(null);
  const lastEmailReasonRef = useRef<string | null>(null);

  const methods = useForm<CreateClientFormValues>({
    defaultValues,
    mode: 'onSubmit',
    resolver: yupResolver(validationSchema) as any,
  });

  const {
    formState: { isSubmitting },
    trigger,
    reset,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    watch,
    getValues,
  } = methods;

  const contactEmailValue = watch('contactEmail');

  const resetEmailCheckCache = useCallback(() => {
    lastCheckedEmailRef.current = null;
    lastEmailCheckRef.current = null;
    lastEmailReasonRef.current = null;
    setIsCheckingEmail(false);
  }, []);

  useEffect(() => {
    if (!open) return;
    if (resumeClient?.organizationId) {
      setCreatedOrganizationId(resumeClient.organizationId);
      setCreatedOrganizationName(resumeClient.organizationName || '');
      setSubscriptionDraft(null);
      setPhase('success');
      return;
    }
    setActiveStep(0);
    setPhase('form');
    setCreatedOrganizationId(null);
    setCreatedOrganizationName('');
    setSubscriptionDraft(null);
    setCopyItems([]);
    resetEmailCheckCache();
    reset(defaultValues);
  }, [
    open,
    resumeClient?.organizationId,
    resumeClient?.organizationName,
    reset,
    resetEmailCheckCache,
  ]);

  const validateUserEmailAvailability = useCallback(
    async (email: string): Promise<boolean> => {
      const trimmed = email?.trim() ?? '';

      if (!trimmed || !EMAIL_FORMAT_REGEX.test(trimmed)) {
        return false;
      }

      if (
        lastCheckedEmailRef.current === trimmed &&
        lastEmailCheckRef.current !== null
      ) {
        if (lastEmailCheckRef.current === false) {
          setError('contactEmail', {
            type: 'manual',
            message: lastEmailReasonRef.current || 'Email already exists',
          });
        }
        return lastEmailCheckRef.current === true;
      }

      setIsCheckingEmail(true);
      try {
        const result = await userApi.checkEmailAvailable({ email: trimmed });
        lastCheckedEmailRef.current = trimmed;
        lastEmailCheckRef.current = result.emailCheck;
        lastEmailReasonRef.current =
          typeof result.reason === 'string' && result.reason.trim()
            ? result.reason
            : null;

        if (result.emailCheck === false) {
          setError('contactEmail', {
            type: 'manual',
            message: lastEmailReasonRef.current || 'Email already exists',
          });
          return false;
        }

        clearErrors('contactEmail');
        return true;
      } catch (error: any) {
        lastCheckedEmailRef.current = null;
        lastEmailCheckRef.current = null;
        lastEmailReasonRef.current = null;

        const errorData = error?.response?.data;
        const backendMessage = Array.isArray(errorData?.message)
          ? String(errorData.message[0] ?? '')
          : typeof errorData?.message === 'string'
            ? errorData.message
            : '';

        const message =
          backendMessage.trim() &&
          !backendMessage.includes('405') &&
          backendMessage.toLowerCase() !== 'internal server error'
            ? backendMessage
            : 'Something went wrong';

        setError('contactEmail', {
          type: 'manual',
          message,
        });
        return false;
      } finally {
        setIsCheckingEmail(false);
      }
    },
    [clearErrors, setError]
  );

  const debouncedEmailCheck = useMemo(
    () =>
      debounce((email: string) => {
        validateUserEmailAvailability(email).catch(() => undefined);
      }, 500),
    [validateUserEmailAvailability]
  );

  useEffect(
    () => () => debouncedEmailCheck.cancel(),
    [debouncedEmailCheck]
  );

  useEffect(() => {
    if (!open || activeStep !== 1 || phase !== 'form') return;

    const trimmed = (contactEmailValue ?? '').trim();
    if (!trimmed || !EMAIL_FORMAT_REGEX.test(trimmed)) {
      debouncedEmailCheck.cancel();
      return;
    }

    debouncedEmailCheck(trimmed);
  }, [activeStep, contactEmailValue, debouncedEmailCheck, open, phase]);

  const handleCountryChange = (selectedCountry: string) => {
    const matchedTimezone = COUNTRY_DEFAULT_TIMEZONE[selectedCountry];
    if (matchedTimezone) {
      setValue('timeZone', matchedTimezone, { shouldDirty: true, shouldValidate: true });
    }
  };

  useEffect(() => {
    let mounted = true;

    const loadOrganizationTypes = async () => {
      if (!open) {
        if (mounted) {
          setOrganizationTypeOptions([]);
          setIsLoadingOrganizationTypes(false);
        }
        return;
      }

      setIsLoadingOrganizationTypes(true);

      try {
        const organizationTypeResponse = await organizationApi.getOrganizationTypes();
        const organizationTypes = Array.isArray(organizationTypeResponse?.data?.data)
          ? organizationTypeResponse.data.data
          : Array.isArray(organizationTypeResponse?.data)
            ? organizationTypeResponse.data
            : [];

        const mappedOptions = organizationTypes
          .map((item: Record<string, any>, index: number) => {
            const optionValue = String(item?.id || item?._id || item?.name || `org-type-${index}`);
            const optionLabel = String(item?.name || item?.label || optionValue);

            return {
              id: optionValue,
              value: optionValue,
              label: optionLabel,
            };
          })
          .filter((option: { label: string }) => option.label.trim().length > 0)
          .filter((option: { label: string }) => option.label.toLowerCase() !== 'reseller');

        if (mounted) {
          setOrganizationTypeOptions(mappedOptions);
        }
      } catch (_error) {
        if (mounted) {
          setOrganizationTypeOptions([]);
        }
      } finally {
        if (mounted) {
          setIsLoadingOrganizationTypes(false);
        }
      }
    };

    loadOrganizationTypes();

    return () => {
      mounted = false;
    };
  }, [open]);

  useEffect(() => {
    if (!open || !parentOrganizationId) {
      setCopyItems([]);
      setIsLoadingCopyItems(false);
      return;
    }

    let mounted = true;
    setIsLoadingCopyItems(true);

    pimApi
      .getPricebooks({
        page: 0,
        pageSize: 500,
        organizationId: parentOrganizationId,
        isActive: true,
      })
      .then((response) => {
        if (!mounted) return;
        const rows = response?.data?.data || response?.data || [];
        setCopyItems(mapPricebooksToCopyItems(Array.isArray(rows) ? rows : []));
      })
      .catch(() => {
        if (!mounted) return;
        setCopyItems([]);
        toast.error('Failed to load products and packages');
      })
      .finally(() => {
        if (mounted) setIsLoadingCopyItems(false);
      });

    return () => {
      mounted = false;
    };
  }, [open, parentOrganizationId]);

  const closeAndReset = () => {
    reset(defaultValues);
    setActiveStep(0);
    setPhase('form');
    setCreatedOrganizationId(null);
    setCreatedOrganizationName('');
    setSubscriptionDraft(null);
    setCopyItems([]);
    resetEmailCheckCache();
    onClose();
  };

  const handleSubscriptionSaved = () => {
    clearPendingClientSubscription();
    onCreated?.();
    closeAndReset();
    navigate('/subscriptions');
  };

  const onCreateClient = async (values: CreateClientFormValues) => {
    const payload = {
      organizationName: values.organizationName.trim(),
      organizationTypeId: values.organizationTypeId,
      telephone: values.telephone?.trim() || undefined,
      email: values.email.trim(),
      website: values.website?.trim() || undefined,
      street: values.street?.trim() || undefined,
      city: values.city?.trim() || undefined,
      country: countryLabelByValue[values.country] || values.country || undefined,
      postalCode: values.postalCode?.trim() || undefined,
      timeZone: values.timeZone || undefined,
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      designation: values.designation?.trim() || undefined,
      language: values.language || undefined,
      contactTelephone: values.contactTelephone?.trim() || undefined,
      contactEmail: values.contactEmail.trim(),
    };

    try {
      const result = await organizationApi.createClient(payload);
      const organizationId = result?.organizationId;
      if (!organizationId) {
        throw new Error('Client created but organization id was missing');
      }

      const selectedItems = copyItems.filter((item) => item.selected && item.productId);
      if (selectedItems.length > 0) {
        try {
          await pimApi.bulkAddPricebooks(
            organizationId,
            selectedItems.map(toCopyPriceBookPayload)
          );
        } catch (_error) {
          toast.error('Client created, but copying products and packages failed');
        }
      }

      setCreatedOrganizationId(organizationId);
      setCreatedOrganizationName(values.organizationName.trim());
      savePendingClientSubscription({
        organizationId,
        organizationName: values.organizationName.trim(),
        user: userData,
      });
      setPhase('success');
      onCreated?.();
    } catch (error: any) {
      toast.error(resolveBackendErrorMessage(error));
      throw error;
    }
  };

  const handleNext = async () => {
    if (activeStep === 0) {
      const valid = await trigger([...COMPANY_STEP_FIELDS]);
      if (!valid) return;
      setActiveStep(1);
      return;
    }

    if (activeStep === 1) {
      const userFieldsValid = await trigger([...USER_STEP_FIELDS]);
      if (!userFieldsValid) return;

      const emailAvailable = await validateUserEmailAvailability(
        getValues('contactEmail')
      );
      if (!emailAvailable) return;

      setActiveStep(2);
      return;
    }

    if (activeStep === 2) {
      if (hasInvalidCopyPrices(copyItems)) {
        toast.error('Copied prices must be greater than or equal to the price book values');
        return;
      }
      setActiveStep(3);
    }
  };

  const handleCreate = async () => {
    if (hasInvalidCopyPrices(copyItems)) {
      toast.error('Copied prices must be greater than or equal to the price book values');
      return;
    }
    await handleSubmit(onCreateClient)();
  };

  const handleToggleCopyItem = (key: string) => {
    setCopyItems((current) =>
      current.map((item) =>
        item.key === key ? { ...item, selected: !item.selected } : item
      )
    );
  };

  const handleCopyMsrpChange = (
    key: string,
    field: 'monthlyMsrp' | 'oneYearMsrp',
    value: string
  ) => {
    setCopyItems((current) =>
      current.map((item) => (item.key === key ? { ...item, [field]: value } : item))
    );
  };

  const wizardFooter =
    phase === 'form' ? (
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Button
          onClick={closeAndReset}
          variant="outlined"
          color="inherit"
          disabled={isSubmitting}
          sx={{ minWidth: 120, height: 42 }}>
          Cancel
        </Button>
        <Box display="flex" style={{ gap: 8 }}>
          {activeStep > 0 && (
            <Button
              onClick={() => setActiveStep((step) => step - 1)}
              variant="outlined"
              color="inherit"
              disabled={isSubmitting}
              sx={{ minWidth: 100, height: 42 }}>
              Back
            </Button>
          )}
          <Button
            onClick={activeStep === LAST_STEP ? handleCreate : handleNext}
            variant="contained"
            color="primary"
            loading={isSubmitting}
            disabled={isSubmitting || isCheckingEmail || isLoadingCopyItems}
            sx={{
              minWidth: 120,
              height: 42,
              borderRadius: '8px',
              fontWeight: 700,
            }}>
            {activeStep === LAST_STEP ? 'Create' : 'Next'}
          </Button>
        </Box>
      </Box>
    ) : phase === 'success' ? (
      <Box display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          color="primary"
          onClick={() => setPhase('subscription')}
          disabled={!createdOrganizationId}
          sx={{ minWidth: 180, height: 42, borderRadius: '8px', fontWeight: 700 }}>
          Create Subscription
        </Button>
      </Box>
    ) : undefined;

  const modalTitle =
    phase === 'form'
      ? 'Create New Client'
      : phase === 'success'
        ? ''
        : `Create subscription for ${createdOrganizationName || 'client'}`;

  return (
    <GenericModal
      open={open}
      onClose={closeAndReset}
      disableClose={phase !== 'form'}
      title={modalTitle}
      width={phase === 'form' || phase === 'success' ? '960px' : '1100px'}
      footer={wizardFooter}>
      {phase === 'success' ? (
        <Box paddingX={1} paddingBottom={1}>
          <Box display="flex" alignItems="flex-start" style={{ gap: 12 }} marginBottom={2}>
            <CheckCircleOutlineIcon
              style={{ color: theme.palette.success.main, fontSize: 36 }}
            />
            <Box>
              <Typography
                style={{
                  fontSize: 22,
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                }}>
                New Client Created Successfully
              </Typography>
              <Typography
                style={{
                  fontSize: 14,
                  color: theme.palette.text.secondary,
                  marginTop: 4,
                }}>
                New client has been created successfully!
              </Typography>
            </Box>
          </Box>

          <Box
            style={{
              borderTop: `1px solid ${theme.palette.divider}`,
              paddingTop: 20,
            }}>
            <Typography
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: theme.palette.text.primary,
                marginBottom: 8,
              }}>
              Great, you&apos;re halfway there!
            </Typography>
            <Typography
              style={{
                fontSize: 14,
                color: theme.palette.text.secondary,
              }}>
              Now let&apos;s set up a subscription for your customer.
            </Typography>
          </Box>
        </Box>
      ) : phase === 'subscription' && createdOrganizationId ? (
        <SubscriptionCreateForm
          clientId={createdOrganizationId}
          clientName={createdOrganizationName}
          initialDraft={subscriptionDraft}
          onNext={(draft) => {
            setSubscriptionDraft(draft);
            setPhase('preview');
          }}
        />
      ) : phase === 'preview' && subscriptionDraft ? (
        <SubscriptionFieldPreview
          draft={subscriptionDraft}
          onBack={() => setPhase('subscription')}
          onSaved={handleSubscriptionSaved}
        />
      ) : (
        <>
          <Typography
            style={{ color: theme.palette.text.secondary, fontSize: 13, marginBottom: 8 }}>
            Configure company, user, pricing and subscription in a few steps
          </Typography>
          <Typography
            style={{ color: theme.palette.text.disabled, fontSize: 12, marginBottom: 16 }}>
            Step {activeStep + 1} of {STEPS.length} — {STEP_CAPTIONS[activeStep]}
          </Typography>

          <WizardStepper activeStep={activeStep} />

          <RHFProvider
            methods={methods}
            onSubmit={async (event) => {
              event?.preventDefault();
              if (activeStep === LAST_STEP) {
                await handleCreate();
                return;
              }
              await handleNext();
            }}>
            {activeStep === 0 && (
              <Box
                style={{
                  marginBottom: 8,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 8,
                  padding: 20,
                }}>
                <SectionTitle
                  title="Company Details"
                  icon={
                    <BusinessOutlinedIcon
                      style={{ color: theme.palette.primary.main, fontSize: 22 }}
                    />
                  }
                />
                <Box display="grid" gridTemplateColumns="1fr 1fr" style={{ gap: 16 }}>
                  <RHFTextField
                    required
                    name="organizationName"
                    outerLabel="COMPANY NAME"
                    placeholder="Company Name *"
                  />
                  <RHFCustomSelect
                    required
                    name="organizationTypeId"
                    outerLabel="COMPANY TYPE"
                    options={organizationTypeOptions}
                    placeholder={
                      isLoadingOrganizationTypes
                        ? 'Loading organization types...'
                        : 'Select type'
                    }
                    disabled={isSubmitting || isLoadingOrganizationTypes}
                  />
                  <RHFTextField
                    name="telephone"
                    outerLabel="COMPANY TELEPHONE"
                    placeholder="Enter phone number"
                  />
                  <RHFTextField
                    required
                    name="email"
                    outerLabel="COMPANY EMAIL"
                    placeholder="Enter email"
                  />
                  <RHFTextField
                    name="website"
                    outerLabel="WEBSITE"
                    placeholder="https://www.example.com"
                  />
                </Box>

                <Box
                  style={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 8,
                    padding: 16,
                    marginTop: 24,
                  }}>
                  <FormLabel
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: theme.palette.text.secondary,
                    }}>
                    LOCATION INFORMATION
                  </FormLabel>
                  <Box
                    display="grid"
                    gridTemplateColumns="1fr 1fr 1fr"
                    style={{ gap: 16, marginTop: 16 }}>
                    <RHFCustomSelect
                      name="country"
                      outerLabel="COUNTRY"
                      options={DEFAULT_COUNTRY_OPTIONS}
                      placeholder="Select country"
                      onChangeCallback={(value) => handleCountryChange(value)}
                    />
                    <RHFTextField name="city" outerLabel="CITY" placeholder="City" />
                    <RHFTextField name="street" outerLabel="STREET" placeholder="Street" />
                    <RHFTextField
                      name="postalCode"
                      outerLabel="POSTAL CODE"
                      placeholder="Postal Code"
                    />
                    <RHFTimezoneAutocomplete name="timeZone" />
                  </Box>
                </Box>
              </Box>
            )}

            {activeStep === 1 && (
              <Box
                style={{
                  marginBottom: 8,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 8,
                  padding: 20,
                }}>
                <SectionTitle
                  title="Client User Information"
                  icon={
                    <PersonOutlineIcon
                      style={{ color: theme.palette.primary.main, fontSize: 22 }}
                    />
                  }
                />
                <Box display="grid" gridTemplateColumns="1fr 1fr" style={{ gap: 16 }}>
                  <RHFTextField
                    required
                    name="firstName"
                    outerLabel="FIRST NAME"
                    placeholder="Enter first name"
                  />
                  <RHFTextField
                    required
                    name="lastName"
                    outerLabel="LAST NAME"
                    placeholder="Enter last name"
                  />
                  <RHFTextField
                    name="designation"
                    outerLabel="DESIGNATION"
                    placeholder="Enter designation"
                  />
                  <RHFCustomSelect
                    name="language"
                    outerLabel="LANGUAGE"
                    options={LANGUAGE_OPTIONS}
                    placeholder="Select language"
                  />
                  <RHFTextField
                    name="contactTelephone"
                    outerLabel="TELEPHONE"
                    placeholder="Enter phone number"
                  />
                  <RHFTextField
                    required
                    name="contactEmail"
                    outerLabel="EMAIL"
                    placeholder="Enter email"
                    EndIcon={
                      isCheckingEmail ? (
                        <InputAdornment position="end">
                          <CircularProgress size={18} />
                        </InputAdornment>
                      ) : undefined
                    }
                  />
                </Box>
              </Box>
            )}
            {activeStep === 2 && (
              <Box
                style={{
                  marginBottom: 8,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 8,
                  padding: 20,
                }}>
                <SectionTitle
                  title="Copy Products and Packages"
                  icon={
                    <Inventory2OutlinedIcon
                      style={{ color: theme.palette.primary.main, fontSize: 22 }}
                    />
                  }
                />
                <CopyProductsAndPackagesStep
                  items={copyItems}
                  loading={isLoadingCopyItems}
                  onToggle={handleToggleCopyItem}
                  onMsrpChange={handleCopyMsrpChange}
                />
              </Box>
            )}
            {activeStep === 3 && (
              <FinalizeStep
                values={getValues()}
                organizationTypeOptions={organizationTypeOptions}
                languageOptions={LANGUAGE_OPTIONS}
                countryLabelByValue={countryLabelByValue}
                copyItems={copyItems}
              />
            )}
          </RHFProvider>
        </>
      )}
    </GenericModal>
  );
};

export default CreateClientWizard;
